import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { copyTextToClipboard } from "../lib/clipboard"

// Copies a value to the OS clipboard and briefly swaps the trigger's label to
// confirm. Used by the Custom Domains DNS instructions so an agent can copy the
// exact TXT name/value and A-record IP without hand-transcribing them.
//
// Usage:
//   button[data-controller="clipboard"
//          data-clipboard-text-value="the text to copy"
//          data-action="clipboard#copy"]
//
// The write mechanism (async Clipboard API + execCommand fallback) lives in
// lib/clipboard.js so controllers that assemble their text at click time can
// share it. This controller stays the canonical trigger for static text.
//
// trackUrl (optional) is POSTed after a successful copy, once per page load —
// the promo toast's "Copy Code" counts copies for the admin screen (#16852).
// Fire and forget: a failed count never touches the copy or its confirmation.
export default class extends Controller {
  static values = {
    text: String,
    confirmLabel: { type: String, default: "Copied!" },
    confirmDuration: { type: Number, default: 1500 },
    announce: String,
    trackUrl: String
  }

  async copy(event) {
    event.preventDefault()

    const copied = await copyTextToClipboard(this.textToCopy)

    if (copied) {
      this.announce(this.hasAnnounceValue ? this.announceValue : "Copied.")
      this.flash()
      this.track()
    } else {
      this.announce("Unable to copy. Select the link and copy it manually.")
      this.flash("Copy failed")
    }
  }

  // Anchors can omit text-value and copy their own href instead. This keeps
  // the copied URL correct when another controller (slug-verify) rewrites the
  // href in place after a rename — a static text-value would go stale.
  get textToCopy() {
    if (this.hasTextValue && this.textValue.length > 0) return this.textValue

    return this.element.href || ""
  }

  track() {
    if (!this.trackUrlValue || this.tracked) return

    this.tracked = true
    post(this.trackUrlValue, { responseKind: "json" }).catch(() => {})
  }

  announce(message) {
    const region = this.element.closest(".gl-public-calendar-urls")?.querySelector("[data-clipboard-status]")
    if (region) region.textContent = message
  }

  flash(label = this.confirmLabelValue) {
    if (this.element.dataset.clipboardBusy) return

    const original = this.element.innerHTML
    this.element.dataset.clipboardBusy = "true"

    // Build the confirmation via DOM nodes so the (potentially config-supplied)
    // label is inserted as text, never parsed as HTML.
    const icon = document.createElement("i")
    icon.className = "ph ph-check me-1"
    this.element.replaceChildren(icon, document.createTextNode(label))

    setTimeout(() => {
      this.element.innerHTML = original
      delete this.element.dataset.clipboardBusy
    }, this.confirmDurationValue)
  }
}
